import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AddFormComponent, ActionButton, FormField } from '@shared/components/add-form/add-form.component';
import { ProductPricingService } from '../services/product-pricing.service';
import { PriceItem, PRICE_CODE_OPTIONS } from '../product-pricing.config';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-product-pricing-add',
  standalone: true,
  imports: [CommonModule, AddFormComponent],
  templateUrl: './product-pricing-add.component.html',
  styleUrls: ['./product-pricing-add.component.css']
})
export class ProductPricingAddComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(AddFormComponent) addForm!: AddFormComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private service = inject(ProductPricingService);
  private translateService = inject(TranslateService);

  title = 'goods.pricing.form.add.title';
  loading = false;
  private skipCanDeactivate = false;

  headerActions: ActionButton[] = [
    { label: 'goods.pricing.form.actions.cancel', action: 'cancel', outlined: true, severity: 'secondary' },
    { label: 'common.actions.create', action: 'save', severity: 'info' }
  ];

  formFields: FormField[] = [
    {
      label: 'goods.pricing.form.fields.price_code',
      controlName: 'value',
      type: 'dropdown',
      placeholder: 'goods.pricing.form.placeholders.select_price_code',
      options: [],
      required: true,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.standard_price_type_derived',
      controlName: 'valueMeaning',
      type: 'text',
      placeholder: '',
      readonly: true,
      disabled: true,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.marketplace_price_name',
      controlName: 'meaning',
      type: 'text',
      placeholder: 'goods.pricing.form.placeholders.enter_marketplace_price_name',
      required: true,
      maxLength: 200,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.remarks',
      controlName: 'description',
      type: 'text',
      placeholder: 'goods.pricing.form.placeholders.enter_remarks',
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.status',
      controlName: 'status',
      type: 'radio',
      options: [
        { label: 'goods.pricing.status.active', value: 1 },
        { label: 'goods.pricing.status.inactive', value: 0 }
      ],
      defaultValue: 1,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.object_version_number',
      controlName: 'objectVersionNumber',
      type: 'number',
      placeholder: 'goods.pricing.form.placeholders.enter_version_number',
      defaultValue: 1,
      required: true,
      min: 1,
      max: 999999,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.order_sequence',
      controlName: 'orderSeq',
      type: 'number',
      placeholder: 'goods.pricing.form.placeholders.enter_order_sequence',
      defaultValue: 0,
      required: true,
      min: 0,
      max: 999999,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.tenant_id',
      controlName: 'tenantId',
      type: 'number',
      placeholder: 'goods.pricing.form.placeholders.enter_tenant_id',
      defaultValue: 0,
      required: true,
      min: 0,
      max: 999999999,
      section: 'goods.pricing.form.sections.basic_information'
    }
  ];

  ngOnInit(): void {
    // Set static dropdown options for price code (same as edit page)
    const staticOptions = PRICE_CODE_OPTIONS;

    // Update form fields with static options
    const idx = this.formFields.findIndex(f => f.controlName === 'value');
    if (idx !== -1) {
      this.formFields = [
        ...this.formFields.slice(0, idx),
        { ...this.formFields[idx], options: staticOptions },
        ...this.formFields.slice(idx + 1)
      ];
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      // Get the current query parameters from the route
      const currentQueryParams = this.route.snapshot.queryParams;
      
      // Navigate back to the list page with preserved query parameters
      this.router.navigate(['/goods/product-pricing'], {
        queryParams: currentQueryParams
      });
      return;
    }
    if (event.action === 'save' && event.data) {
      const raw = event.data;
      const payload: PriceItem = {
        creationDate: null,
        createdBy: null,
        lastUpdateDate: null,
        lastUpdatedBy: null,
        _token: '',
        lovValueId: 0, // Will be assigned by the API
        lovId: 200575028642603000,
        lovCode: 'PRICE.ITEM.TYPE',
        objectVersionNumber: raw.objectVersionNumber,
        value: raw.value,
        meaning: raw.meaning,
        description: raw.description ?? '',
        tenantId: raw.tenantId,
        orderSeq: raw.orderSeq,
        status: raw.status,
      };
      this.loading = true;
      this.service.createPriceItem(payload).subscribe((success) => {
        this.loading = false;
        if (success) {
          this.skipCanDeactivate = true;
          this.addForm?.form?.markAsPristine();
          // Get the current query parameters from the route
          const currentQueryParams = this.route.snapshot.queryParams;
          
          // Navigate back to the list page with preserved query parameters
          this.router.navigate(['/goods/product-pricing'], {
            queryParams: currentQueryParams
          });
        } else {
          // Handle error - could show a toast message here
          console.error('Failed to create price item');
        }
      });
    }
  }

  onFormValueChanges(value: any): void {
    const val = value?.value;
    if (!this.addForm || !val) return;
    const derivedKey = this.service.getValueMeaningFromLov(val);
    if (derivedKey) {
      // Translate the i18n key to the actual translated value
      const translatedValue = this.translateService.instant(derivedKey);
      const current = this.addForm.form.get('valueMeaning')?.value;
      if (translatedValue && translatedValue !== current) {
        this.addForm.form.get('valueMeaning')?.setValue(translatedValue);
      }
    }
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading) {
      return true;
    }

    return !(this.addForm?.form?.dirty ?? false);
  }
}
